import { useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { CalendarIcon, ArrowRight, CheckCircle2 } from "lucide-react";
import { motion } from "framer-motion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { Reveal, Label } from "./Reveal";
import { SERVICE_OPTIONS, CONTACT } from "../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD =
  "w-full bg-transparent border-b border-border text-white placeholder-zinc-600 py-4 text-lg font-light focus:outline-none focus:border-acid transition-colors";

export default function QuoteForm() {
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    service_type: "",
    property_type: "",
    property_size: "",
    location: "",
    message: "",
  });
  const [date, setDate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service_type) {
      toast.error("Please complete name, email, phone and service.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        message: date
          ? `${form.message}${form.message ? " — " : ""}Preferred start: ${format(
              date,
              "PPP"
            )}`
          : form.message,
      };
      await axios.post(`${API}/quotes`, payload);
      setDone(true);
      toast.success("Quote request received. We'll be in touch shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="quote"
      data-testid="quote-section"
      className="bg-obsidian py-24 lg:py-32 border-b border-border"
    >
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-4">
          <Reveal>
            <Label>Start Here</Label>
            <h2 className="font-display uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[0.9] text-white">
              Request<br />a Quote.
            </h2>
            <p className="mt-8 text-zinc-400 font-light leading-relaxed">
              Tell us about your facility and we&apos;ll build a tailored scope and
              price. No obligation, no pressure.
            </p>
            <div className="mt-10 space-y-4 border-t border-border pt-8">
              <a
                href={CONTACT.phoneHref}
                className="block text-white hover:text-acid transition-colors"
              >
                <span className="text-xs uppercase tracking-[0.2em] text-zinc-500 block mb-1">
                  Call
                </span>
                <span className="font-display text-2xl">{CONTACT.phone}</span>
              </a>
              <a
                href={CONTACT.emailHref}
                className="block text-white hover:text-acid transition-colors break-all"
              >
                <span className="text-xs uppercase tracking-[0.2em] text-zinc-500 block mb-1">
                  Email
                </span>
                <span className="font-display text-lg">{CONTACT.email}</span>
              </a>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-8">
          {done ? (
            <motion.div
              data-testid="quote-success"
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              className="border border-acid p-12 flex flex-col items-start"
            >
              <CheckCircle2 className="text-acid mb-6" size={48} />
              <h3 className="font-display uppercase text-3xl text-white tracking-tight">
                Request received.
              </h3>
              <p className="mt-4 text-zinc-400 font-light max-w-md">
                Thanks {form.name.split(" ")[0]}. A member of our team will reach
                out within one business day to arrange your site assessment.
              </p>
              <button
                data-testid="quote-reset"
                onClick={() => {
                  setDone(false);
                  setForm({
                    name: "",
                    company: "",
                    email: "",
                    phone: "",
                    service_type: "",
                    property_type: "",
                    property_size: "",
                    location: "",
                    message: "",
                  });
                  setDate(null);
                }}
                className="mt-8 text-acid text-xs uppercase tracking-[0.2em] font-bold hover:underline"
              >
                Submit another request
              </button>
            </motion.div>
          ) : (
            <form onSubmit={submit} data-testid="quote-form" className="space-y-10">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <input
                  data-testid="quote-name"
                  className={FIELD}
                  placeholder="Full name *"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                />
                <input
                  data-testid="quote-company"
                  className={FIELD}
                  placeholder="Company"
                  value={form.company}
                  onChange={(e) => update("company", e.target.value)}
                />
                <input
                  data-testid="quote-email"
                  type="email"
                  className={FIELD}
                  placeholder="Email *"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                />
                <input
                  data-testid="quote-phone"
                  className={FIELD}
                  placeholder="Phone *"
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <span className="text-xs uppercase tracking-[0.2em] text-zinc-500 block mb-3">
                    Service required *
                  </span>
                  <Select
                    value={form.service_type}
                    onValueChange={(v) => update("service_type", v)}
                  >
                    <SelectTrigger
                      data-testid="quote-service"
                      className="w-full bg-transparent border-0 border-b border-border rounded-none text-white py-6 text-lg focus:ring-0 focus:border-acid"
                    >
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent className="bg-elevated border-border text-white rounded-none">
                      {SERVICE_OPTIONS.map((s) => (
                        <SelectItem
                          key={s}
                          value={s}
                          data-testid={`service-opt-${s}`}
                          className="focus:bg-acid focus:text-obsidian rounded-none"
                        >
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <span className="text-xs uppercase tracking-[0.2em] text-zinc-500 block mb-3">
                    Preferred start date
                  </span>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        data-testid="quote-date"
                        className="w-full flex items-center justify-between bg-transparent border-b border-border text-left py-4 text-lg font-light text-white hover:border-acid transition-colors"
                      >
                        <span className={date ? "text-white" : "text-zinc-600"}>
                          {date ? format(date, "PPP") : "Pick a date"}
                        </span>
                        <CalendarIcon size={18} className="text-acid" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-auto p-0 bg-elevated border-border rounded-none"
                      align="start"
                    >
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={setDate}
                        initialFocus
                        disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <input
                  data-testid="quote-size"
                  className={FIELD}
                  placeholder="Approx. facility size (sqm)"
                  value={form.property_size}
                  onChange={(e) => update("property_size", e.target.value)}
                />
                <input
                  data-testid="quote-location"
                  className={FIELD}
                  placeholder="Suburb / location"
                  value={form.location}
                  onChange={(e) => update("location", e.target.value)}
                />
              </div>

              <textarea
                data-testid="quote-message"
                className={`${FIELD} resize-none`}
                rows={3}
                placeholder="Tell us about your facility & requirements"
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
              />

              <button
                type="submit"
                data-testid="quote-submit"
                disabled={submitting}
                className="group inline-flex items-center gap-3 bg-acid text-obsidian px-10 py-5 text-xs uppercase tracking-[0.2em] font-bold hover:bg-white transition-colors disabled:opacity-60"
              >
                {submitting ? "Sending..." : "Send Request"}
                <ArrowRight
                  size={16}
                  className="group-hover:translate-x-1 transition-transform"
                />
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
